import { Box } from "@mui/material";
import { useEffect, useRef } from "react";

const SCENE_HEIGHT = 600;

export const RetrowaveBackground = () => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const drawnRef = useRef(false);

	useEffect(() => {
		if (drawnRef.current) return;
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		const dpr = window.devicePixelRatio || 1;
		const cssW = canvas.parentElement?.clientWidth ?? window.innerWidth;
		const cssH = SCENE_HEIGHT;
		canvas.width = cssW * dpr;
		canvas.height = cssH * dpr;
		canvas.style.width = `${cssW}px`;
		canvas.style.height = `${cssH}px`;
		ctx.scale(dpr, dpr);

		const w = cssW;
		const h = cssH;
		const horizonY = h * 0.48;
		const vanishX = w * 0.5;

		// ── Sky gradient ──
		const skyGrad = ctx.createLinearGradient(0, 0, 0, horizonY);
		skyGrad.addColorStop(0, "rgba(10, 2, 30, 0.12)");
		skyGrad.addColorStop(0.5, "rgba(30, 10, 60, 0.08)");
		skyGrad.addColorStop(1, "rgba(60, 20, 80, 0.04)");
		ctx.fillStyle = skyGrad;
		ctx.fillRect(0, 0, w, horizonY);

		// ── Stars ──
		const starCount = 15;
		const seededRandom = (seed: number) => {
			const x = Math.sin(seed * 9301 + 49297) * 49297;
			return x - Math.floor(x);
		};
		const drawStar = (
			cx: number,
			cy: number,
			outerR: number,
			alpha: number,
		) => {
			const innerR = outerR * 0.15;
			ctx.beginPath();
			for (let i = 0; i < 8; i++) {
				const r = i % 2 === 0 ? outerR : innerR;
				const angle = (i * Math.PI) / 4 - Math.PI / 2;
				const px = cx + Math.cos(angle) * r;
				const py = cy + Math.sin(angle) * r;
				if (i === 0) ctx.moveTo(px, py);
				else ctx.lineTo(px, py);
			}
			ctx.closePath();
			ctx.fillStyle = `rgba(200, 180, 255, ${alpha})`;
			ctx.fill();
		};
		for (let i = 0; i < starCount; i++) {
			const sx = seededRandom(i * 3 + 1) * w;
			const sy = seededRandom(i * 3 + 2) * horizonY * 0.85;
			const sr = 5 + seededRandom(i * 3 + 3) * 8;
			const sa = 0.4 + seededRandom(i * 3 + 4) * 0.5;
			drawStar(sx, sy, sr, sa);
		}

		// ── Sun ──
		const sunR = Math.min(w, h) * 0.14;
		const sunCx = vanishX;
		const sunCy = horizonY;

		// Sun glow
		const glowGrad = ctx.createRadialGradient(
			sunCx,
			sunCy,
			sunR * 0.2,
			sunCx,
			sunCy,
			sunR * 2.5,
		);
		glowGrad.addColorStop(0, "rgba(255, 120, 50, 0.10)");
		glowGrad.addColorStop(0.4, "rgba(255, 60, 100, 0.04)");
		glowGrad.addColorStop(1, "rgba(255, 60, 100, 0)");
		ctx.fillStyle = glowGrad;
		ctx.fillRect(0, 0, w, horizonY + 20);

		// Sun disc with gradient (half circle above horizon)
		ctx.save();
		ctx.beginPath();
		ctx.rect(0, 0, w, horizonY);
		ctx.clip();
		ctx.beginPath();
		ctx.arc(sunCx, sunCy, sunR, 0, Math.PI * 2);
		ctx.clip();

		const sunGrad = ctx.createLinearGradient(
			sunCx,
			sunCy - sunR,
			sunCx,
			sunCy + sunR,
		);
		sunGrad.addColorStop(0, "rgba(255, 180, 50, 0.25)");
		sunGrad.addColorStop(0.4, "rgba(255, 100, 60, 0.22)");
		sunGrad.addColorStop(0.7, "rgba(255, 50, 100, 0.20)");
		sunGrad.addColorStop(1, "rgba(200, 30, 120, 0.18)");
		ctx.fillStyle = sunGrad;
		ctx.fill();

		// Sun horizontal scan lines
		ctx.fillStyle = "rgba(250, 250, 250, 0.5)";
		const scanLineCount = 8;
		for (let i = 0; i < scanLineCount; i++) {
			const frac = (i + 1) / (scanLineCount + 1);
			const ly = sunCy - sunR + frac * sunR * 2;
			const gap = 1.5 + i * 0.8;
			ctx.fillRect(sunCx - sunR, ly, sunR * 2, gap);
		}
		ctx.restore();

		// ── Mountains (wireframe) ──
		const drawMountainRange = (
			peaks: [number, number][],
			color: string,
			lineW: number,
		) => {
			const first = peaks[0] as [number, number];
			const last = peaks[peaks.length - 1] as [number, number];

			// Fill below mountain with page bg to occlude sun
			ctx.beginPath();
			ctx.moveTo(first[0], first[1]);
			for (const p of peaks.slice(1)) {
				ctx.lineTo(p[0], p[1]);
			}
			ctx.lineTo(last[0], horizonY + 2);
			ctx.lineTo(first[0], horizonY + 2);
			ctx.closePath();
			ctx.fillStyle = "rgba(250, 250, 250, 0.92)";
			ctx.fill();

			// Outline
			ctx.beginPath();
			ctx.moveTo(first[0], first[1]);
			for (const p of peaks.slice(1)) {
				ctx.lineTo(p[0], p[1]);
			}
			ctx.strokeStyle = color;
			ctx.lineWidth = lineW;
			ctx.lineJoin = "round";
			ctx.stroke();

			// Horizontal grid lines on the mountains
			const minY = Math.min(...peaks.map((p) => p[1]));
			const gridSpacing = 12;
			for (let gy = minY + gridSpacing; gy < horizonY; gy += gridSpacing) {
				const intersections: number[] = [];
				for (let i = 0; i < peaks.length - 1; i++) {
					const p1 = peaks[i] as [number, number];
					const p2 = peaks[i + 1] as [number, number];
					const [x1, y1] = p1;
					const [x2, y2] = p2;
					if ((y1 <= gy && y2 >= gy) || (y1 >= gy && y2 <= gy)) {
						const t = (gy - y1) / (y2 - y1);
						intersections.push(x1 + t * (x2 - x1));
					}
				}
				intersections.sort((a, b) => a - b);
				ctx.strokeStyle = color;
				ctx.lineWidth = 0.6;
				for (let k = 0; k < intersections.length - 1; k += 2) {
					const kx = intersections[k] as number;
					const kx1 = intersections[k + 1] as number;
					ctx.beginPath();
					ctx.moveTo(kx, gy);
					ctx.lineTo(kx1, gy);
					ctx.stroke();
				}
			}
		};

		// Back mountain range (further, lighter)
		const backLeft: [number, number][] = [
			[0, horizonY],
			[w * 0.05, horizonY - 70],
			[w * 0.11, horizonY - 120],
			[w * 0.17, horizonY - 75],
			[w * 0.24, horizonY - 150],
			[w * 0.3, horizonY - 100],
			[w * 0.37, horizonY - 45],
			[w * 0.43, horizonY],
		];
		const backRight: [number, number][] = [
			[w * 0.57, horizonY],
			[w * 0.62, horizonY - 40],
			[w * 0.69, horizonY - 90],
			[w * 0.75, horizonY - 130],
			[w * 0.82, horizonY - 70],
			[w * 0.88, horizonY - 105],
			[w * 0.94, horizonY - 55],
			[w, horizonY],
		];
		drawMountainRange(backLeft, "rgba(100, 60, 200, 0.12)", 1.2);
		drawMountainRange(backRight, "rgba(100, 60, 200, 0.12)", 1.2);

		// Front mountain range (closer, more opaque)
		const frontLeft: [number, number][] = [
			[0, horizonY],
			[w * 0.06, horizonY - 35],
			[w * 0.13, horizonY - 80],
			[w * 0.19, horizonY - 50],
			[w * 0.26, horizonY - 95],
			[w * 0.33, horizonY - 40],
			[w * 0.39, horizonY - 20],
			[w * 0.45, horizonY],
		];
		const frontRight: [number, number][] = [
			[w * 0.55, horizonY],
			[w * 0.6, horizonY - 25],
			[w * 0.67, horizonY - 55],
			[w * 0.72, horizonY - 85],
			[w * 0.79, horizonY - 45],
			[w * 0.85, horizonY - 75],
			[w * 0.91, horizonY - 30],
			[w, horizonY],
		];
		drawMountainRange(frontLeft, "rgba(60, 40, 180, 0.16)", 1.5);
		drawMountainRange(frontRight, "rgba(60, 40, 180, 0.16)", 1.5);

		// ── Horizon line ──
		ctx.strokeStyle = "rgba(120, 60, 220, 0.15)";
		ctx.lineWidth = 1.5;
		ctx.beginPath();
		ctx.moveTo(0, horizonY);
		ctx.lineTo(w, horizonY);
		ctx.stroke();

		// ── Ground perspective grid ──
		const gridBottom = h;
		const gridRows = 18;
		const gridCols = 28;

		// Vertical lines converging to vanishing point
		for (let i = 0; i <= gridCols; i++) {
			const xFrac = (i / gridCols) * 2 - 1;
			const bottomX = vanishX + xFrac * w * 1.2;
			const edgeFade = 1 - Math.abs(xFrac) * 0.4;
			const alpha = edgeFade * 0.1;
			ctx.strokeStyle = `rgba(80, 50, 200, ${alpha})`;
			ctx.lineWidth = 0.8;
			ctx.beginPath();
			ctx.moveTo(vanishX, horizonY);
			ctx.lineTo(bottomX, gridBottom);
			ctx.stroke();
		}

		// Horizontal lines with exponential perspective spacing
		for (let j = 1; j <= gridRows; j++) {
			const rowFrac = j / gridRows;
			const perspFrac = rowFrac * rowFrac;
			const y = horizonY + perspFrac * (gridBottom - horizonY);
			const alpha = 0.03 + rowFrac * 0.09;
			ctx.strokeStyle = `rgba(80, 50, 200, ${alpha})`;
			ctx.lineWidth = 0.8;
			ctx.beginPath();
			ctx.moveTo(0, y);
			ctx.lineTo(w, y);
			ctx.stroke();
		}

		// ── Fade to page background at bottom ──
		const fadeGrad = ctx.createLinearGradient(0, h - 100, 0, h);
		fadeGrad.addColorStop(0, "rgba(250, 250, 250, 0)");
		fadeGrad.addColorStop(1, "rgba(250, 250, 250, 1)");
		ctx.fillStyle = fadeGrad;
		ctx.fillRect(0, h - 100, w, 100);

		drawnRef.current = true;
	}, []);

	return (
		<Box
			sx={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				height: SCENE_HEIGHT,
				pointerEvents: "none",
				zIndex: 0,
			}}
		>
			<canvas
				ref={canvasRef}
				style={{
					width: "100%",
					height: "100%",
					opacity: 0.6,
				}}
			/>
		</Box>
	);
};
